import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { api } from "@/lib/api-client";
import { useForm } from "@mantine/form";
import {
  Button,
  Chip,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Tab,
  Tabs,
  useDisclosure,
} from "@heroui/react";
import { notifications } from "@mantine/notifications";
import {
  getPaymentTypeLabel,
  PaymentType,
  preparePrintData,
} from "@/utils/pos-printer/index";
import {
  PrintReceiptType,
  usePosPrinterStore,
} from "@/stores/pos-printer-store";
import { toast } from "sonner";
import { useEffect } from "react";
import { SplitPaymentForm, SplitPaymentModal } from "./POS/SplitPaymentModal";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { useNavigate } from "react-router";
import {
  Download,
  FileCheck2,
  FileX2,
  Printer,
  PrinterCheck,
  Replace,
  Send,
} from "lucide-react";

export const InvoiceDetailsModal = ({
  isAuthenticated,
  invoiceId,
  onClose,
}: {
  isAuthenticated: boolean;
  invoiceId?: string;
  onClose: () => void;
}) => {
  const navigate = useNavigate();

  const {
    isOpen: isChangePaymentsModalOpen,
    onOpen: openChangePaymentsModal,
    onClose: closeChangePaymentsModal,
  } = useDisclosure();

  const {
    data: invoice,
    isPending,
    isFetching,
    isError,
    refetch,
    processedErrorMessage: getInvoiceErrorMessage,
  } = api.taxRegister.useGetOrganizationInvoice({
    invoiceId,
    supportsDiacritics: false,
  });

  const { isPrinting, printReceipt, connectToDevice, selectedPrinterType } =
    usePosPrinterStore((state) => state);

  const {
    mutateAsync: postSendInvoiceEmail,
    isPending: isPostSendInvoiceEmailPending,
    processedErrorMessage: postSendInvoiceEmailErrorMessage,
  } = api.taxRegister.usePostSendInvoiceEmail();

  const {
    mutateAsync: postCancelInvoice,
    isPending: isPostCancelInvoicePending,
    processedErrorMessage: postCancelInvoiceErrorMessage,
  } = api.taxRegister.usePostCancelInvoice();

  const {
    mutateAsync: putOrganizationInvoicePrintCount,
    isPending: isPutOrganizationInvoicePrintCountPending,
    processedErrorMessage: putOrganizationInvoicePrintCountErrorMessage,
  } = api.taxRegister.usePutOrganizationInvoicePrintCount();

  const {
    mutateAsync: putInvoicePaymentMethod,
    isPending: isPutInvoicePaymentMethodPending,
    processedErrorMessage: putInvoicePaymentMethodErrorMessage,
  } = api.taxRegister.usePutInvoicePaymentMethod();

  const {
    mutateAsync: getDownloadInvoice,
    isPending: isGetDownloadInvoicePending,
    processedErrorMessage: getDownloadInvoiceErrorMessage,
  } = api.taxRegister.useGetDownloadInvoice();

  const {
    data: locationsData,
    processedErrorMessage: getLocationsErrorMessage,
  } = api.location.useGetLocations({
    page: 1,
    perPage: 100,
    sortBy: "labelDesc",
  });

  const handlePrintReceipt = async ({ type }: { type: PrintReceiptType }) => {
    if (!invoice) {
      notifications.show({
        message: t`Invoice not found`,
      });
      return;
    }

    const printerConfig =
      selectedPrinterType === "qztray"
        ? locationsData?.locations.find(
            (location) => location.locationId == invoice.locationId,
          )?.PosPrinter || null
        : null;

    const printReceiptPromise = async () => {
      await connectToDevice({ shouldThrow: true });

      const printData =
        type === "receipt"
          ? await preparePrintData(
              {
                type: "invoice",
                invoiceData: invoice,
                isCopy: true,
              },
              printerConfig,
            )
          : await preparePrintData(
              {
                type: "transaction-account",
                data: {
                  ...invoice,
                  amount: invoice.totalDue,
                  amountFormatted: invoice.totalDueFormatted,
                  currency: invoice.currencyId,
                  invoiceNumber: invoice.number,
                  name: invoice.additionalReceiptData._documentIssuer.name,
                  address:
                    invoice.additionalReceiptData._documentIssuer.address,
                  zip: invoice.additionalReceiptData._documentIssuer.zip,
                  city: invoice.additionalReceiptData._documentIssuer.city,
                  country:
                    invoice.additionalReceiptData._documentIssuer.country,
                  IBAN: invoice.additionalReceiptData._documentIssuer.IBAN,
                  SWIFT: invoice.additionalReceiptData._documentIssuer.SWIFT,
                  reference: `SI00 ${invoice.numberOnly}`,
                  dateDue: invoice.dateDue,
                },
              },
              printerConfig,
            );

      await printReceipt({
        printData,
        printerConfig,
        shouldThrow: true,
      });

      await putOrganizationInvoicePrintCount(invoiceId);
    };

    toast.promise(printReceiptPromise, {
      loading:
        invoice?.type === "invoice"
          ? t`Račun se tiska...`
          : t`Predračun se tiska...`,
      success: async () => {
        await refetch();
        return invoice?.type === "invoice"
          ? t`Račun je bil tiskan`
          : t`Predračun je bil tiskan`;
      },
      error: (data) => {
        return (
          data?.toString() ||
          (invoice?.type === "invoice"
            ? t`Tiskanje računa ni uspelo`
            : t`Tiskanje predračuna ni uspelo`)
        );
      },
    });
  };

  const form = useForm<{
    cancellationReason: string;
  }>({
    initialValues: {
      cancellationReason: "",
    },
    validate: {
      cancellationReason: (value) => {
        if (invoice?.type === "estimate") {
          return null;
        }

        return value.length > 0 ? null : t`Razlog stornacije je obvezen`;
      },
    },
  });

  useEffect(() => {
    if (!invoiceId) {
      form.reset();
    }
  }, [invoiceId]);

  return (
    <>
      <Modal
        size="xl"
        isOpen={invoiceId != null && isAuthenticated}
        onClose={onClose}
        scrollBehavior="outside"
        data-identifier="invoice-details-modal"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {invoice?.typeLocalized} {invoice?.number}
              </ModalHeader>
              <ModalBody>
                <LimeAlert message={getLocationsErrorMessage} />
                <LimeAlert
                  message={putOrganizationInvoicePrintCountErrorMessage}
                />

                {/** Disable animation because it is inside a modal, otherwise modal does not close correctly */}
                <Tabs fullWidth disableAnimation>
                  <Tab
                    title={invoice?.type === "invoice" ? `Račun` : `Predračun`}
                    className="flex flex-col gap-3"
                  >
                    <InvoiceDetailRow
                      label={
                        invoice?.type === "invoice"
                          ? t`Številka računa`
                          : t`Številka predračuna`
                      }
                      value={invoice?.number || "XXXX-XXXXX"}
                      isSkeleton={isPending}
                    />
                    {invoice?.canceledInvoiceNumber ? (
                      <InvoiceDetailRow
                        label={
                          invoice?.type === "invoice"
                            ? t`Številka storniranega računa`
                            : t`Številka storniranega predračuna`
                        }
                        value={invoice?.canceledInvoiceNumber || "XXXX-XXXXX"}
                        isSkeleton={isPending}
                      />
                    ) : undefined}
                    <InvoiceDetailRow
                      label={t`Datum in čas`}
                      value={invoice?.date || "XX. XX. XXXX XX:XX"}
                      isSkeleton={isPending}
                    />
                    <InvoiceDetailRow
                      label={t`Datum zapadlosti`}
                      value={invoice?.dateDueFormatted || "XX. XX. XXXX XX:XX"}
                      isSkeleton={isPending}
                    />
                    <InvoiceDetailRow
                      label={
                        invoice?.canceled ? t`Originalni znesek` : t`Znesek`
                      }
                      value={invoice?.totalFormatted || "XX.XXX,XX X"}
                      isSkeleton={isPending}
                    />
                    {invoice?.creditNoteTotalFormatted ? (
                      <InvoiceDetailRow
                        label={t`Dobropis`}
                        value={
                          invoice?.creditNoteTotalFormatted || "XX.XXX,XX X"
                        }
                        isSkeleton={isPending}
                      />
                    ) : undefined}
                    {invoice?.type === "estimate" ? null : (
                      <InvoiceDetailRow
                        label={t`Način plačila`}
                        value={
                          invoice ? (
                            <>
                              {invoice.additionalReceiptData.payments.map(
                                (payment) => (
                                  <div
                                    key={payment.type}
                                    className="flex w-full justify-between gap-2 text-sm text-gray-500"
                                  >
                                    <p className="w-fit text-left">
                                      {getPaymentTypeLabel(
                                        payment.type as PaymentType,
                                      )}
                                      {payment.type === "coupon" ? (
                                        <Link
                                          href={`/dashboard/gift-card/${payment.giftCardId}`}
                                          showAnchorIcon
                                          className="ml-1 w-fit text-left"
                                          size="sm"
                                        >
                                          {payment.code}
                                        </Link>
                                      ) : undefined}
                                    </p>
                                    <p className="w-full flex-1 text-right">
                                      {payment.amountFormatted}
                                    </p>
                                  </div>
                                ),
                              )}
                              {invoice.additionalReceiptData.payments.length ===
                                0 && (
                                <p className="text-sm text-gray-500">
                                  <Trans>Ni plačila</Trans>
                                </p>
                              )}
                            </>
                          ) : (
                            "XXXXXXXXX"
                          )
                        }
                        isSkeleton={isPending}
                      />
                    )}
                    {invoice?.type === "estimate" ? null : (
                      <InvoiceDetailRow
                        label={
                          invoice?.type === "invoice"
                            ? t`Stanje računa`
                            : t`Stanje predračuna`
                        }
                        value={(() => {
                          const color = (() => {
                            switch (invoice?.status.value) {
                              case "paid":
                              case "payment_not_required":
                                return "success";
                              case "paid_partially":
                                return "warning";
                              case "canceled":
                                return "danger";
                              case "not_paid":
                                return "danger";
                              default:
                                return "default";
                            }
                          })();

                          return (
                            <Chip
                              size="sm"
                              color={color}
                              variant="flat"
                              data-identifier={invoice?.status.value}
                            >
                              {invoice?.status.label}
                            </Chip>
                          );
                        })()}
                        isSkeleton={isPending}
                      />
                    )}

                    <InvoiceDetailRow
                      label={t`Izdal`}
                      value={
                        invoice?.additionalReceiptData.employeeData.name ||
                        "XXXX XXXXX"
                      }
                      isSkeleton={isPending}
                    />

                    {invoice?._documentClient.customerData && (
                      <InvoiceDetailRow
                        label={t`Končna stranka`}
                        value={
                          invoice._documentClient.customerData.name ||
                          "XXXX XXXXX"
                        }
                        isSkeleton={isPending}
                      />
                    )}

                    {invoice?._documentClient.companyData && (
                      <InvoiceDetailRow
                        label={t`Končno podjetje`}
                        value={
                          invoice._documentClient.companyData.companyName ||
                          "XXXX XXXXX"
                        }
                        isSkeleton={isPending}
                      />
                    )}

                    {invoice?.referencedDocuments?.length ? (
                      <>
                        <Divider />
                        <p className="font-semibold">
                          <Trans>Povezani dokumenti</Trans>
                        </p>
                        {invoice?.referencedDocuments?.map((document) => (
                          <InvoiceDetailRow
                            label={
                              document.type === "estimate"
                                ? t`Predračun`
                                : t`Račun`
                            }
                            value={
                              <Link
                                showAnchorIcon
                                className="text-sm"
                                href={`/dashboard/tax-register/invoice/${document.id}`}
                              >
                                {document.number}
                              </Link>
                            }
                            isSkeleton={isPending}
                          />
                        ))}
                      </>
                    ) : null}

                    {invoice?.canceled && (
                      <div>
                        <p className="font-semibold uppercase text-red-500">
                          {invoice.type === "invoice" ? (
                            <Trans>Storniran</Trans>
                          ) : (
                            <Trans>Uporabljen</Trans>
                          )}{" "}
                          {invoice.canceledInvoiceNumber
                            ? `- ${invoice.canceledInvoiceNumber}`
                            : ""}
                        </p>
                        {invoice.cancellationReason != null && (
                          <p className="text-sm text-gray-500">
                            {invoice.cancellationReason}
                          </p>
                        )}
                      </div>
                    )}

                    <Divider />

                    <p className="font-semibold">
                      <Trans>Artikli</Trans>
                    </p>
                    {invoice?.additionalReceiptData.invoiceData.items.map(
                      (item) => (
                        <div
                          key={item.name}
                          className="flex w-full justify-between gap-2 text-sm text-gray-500"
                        >
                          <p className="w-fit text-left">
                            {item.quantity}x {item.name}
                          </p>
                          <p className="w-full flex-1 text-right">
                            {item.totalWithTaxFormatted}
                          </p>
                        </div>
                      ),
                    )}

                    {invoice && invoice.giftCards.length > 0 ? (
                      <>
                        <Divider />

                        <p className="font-semibold">
                          <Trans>Izdani darilni boni</Trans>
                        </p>
                        {invoice.giftCards.map((giftCard) => (
                          <div
                            key={giftCard.code}
                            className="flex w-full justify-between gap-2 text-sm text-gray-500"
                          >
                            <Link
                              href={`/dashboard/gift-card/${giftCard.id}`}
                              showAnchorIcon
                              className="w-fit text-left"
                            >
                              {giftCard.code}
                            </Link>
                            <p className="w-full flex-1 text-right">
                              {giftCard.initialAmountFormatted}
                            </p>
                          </div>
                        ))}
                      </>
                    ) : undefined}
                  </Tab>
                  <Tab title={t`Aktivnost`} className="flex flex-col gap-4">
                    {invoice?.additionalReceiptData.activities.map(
                      (activity) => (
                        <div>
                          <InvoiceDetailRow
                            label={activity.label}
                            key={activity.label}
                            value={activity.date}
                          />
                          {activity.description && (
                            <p className="text-xs text-gray-500">
                              {activity.description}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </Tab>
                </Tabs>

                <Divider />
              </ModalBody>
              <ModalFooter className="flex flex-col">
                {invoice?.type === "estimate" ? (
                  <>
                    <Button
                      color="primary"
                      className="h-12"
                      data-identifier="convert-estimate-to-invoice"
                      startContent={<FileCheck2 size={16} />}
                      as={Link}
                      href={`/dashboard/tax-register/invoice/new?fromEstimate=${invoice.id}`}
                    >
                      Pretvori v račun
                    </Button>

                    <Divider className="my-2" />
                  </>
                ) : undefined}

                {invoice?.type === "estimate" ? null : (
                  <>
                    <Button
                      isDisabled={
                        isPending ||
                        isError ||
                        isPostCancelInvoicePending ||
                        isPostSendInvoiceEmailPending ||
                        isPutOrganizationInvoicePrintCountPending
                      }
                      isLoading={isPrinting || isFetching}
                      className="h-12"
                      color="primary"
                      onPress={() => handlePrintReceipt({ type: "receipt" })}
                      data-identifier="print-receipt"
                      startContent={<PrinterCheck size={16} />}
                    >
                      <Trans>Ponovno tiskaj</Trans>
                    </Button>
                    {invoice?.additionalReceiptData.payments.length === 0 &&
                      !invoice.paidInFull && (
                        <Button
                          isDisabled={
                            isPending ||
                            isError ||
                            isPostCancelInvoicePending ||
                            isPostSendInvoiceEmailPending
                          }
                          isLoading={
                            isPrinting ||
                            isFetching ||
                            isPutOrganizationInvoicePrintCountPending
                          }
                          className="h-12"
                          color="primary"
                          onPress={() => handlePrintReceipt({ type: "UPN" })}
                          data-identifier="print-bank-receipt"
                          startContent={<Printer size={16} />}
                        >
                          <Trans>Tiskaj UPN listek</Trans>
                        </Button>
                      )}
                    {invoice?.additionalReceiptData.payments.length === 0 ? (
                      <Button
                        isDisabled={
                          isPending ||
                          isError ||
                          isPostCancelInvoicePending ||
                          isPostSendInvoiceEmailPending
                        }
                        isLoading={isFetching}
                        className="h-12"
                        color="primary"
                        data-identifier="confirm-payment"
                        onPress={async () => {
                          await putInvoicePaymentMethod({
                            invoiceId: invoice.id,
                            body: {
                              payments: [
                                {
                                  type: "bank",
                                  amountCents: Math.ceil(
                                    invoice.totalWithTax * 100,
                                  ),
                                },
                              ],
                            },
                          });

                          refetch();
                        }}
                        startContent={<FileCheck2 size={16} />}
                      >
                        <Trans>Potrdi plačilo</Trans>
                      </Button>
                    ) : undefined}
                  </>
                )}

                {invoice &&
                !invoice.canceled &&
                (invoice.additionalReceiptData.payments || []).length > 0 &&
                invoice.additionalReceiptData.payments.find(
                  (payment) => payment.type !== "bank",
                ) ? (
                  <Button
                    isDisabled={
                      isPending ||
                      isError ||
                      isPostCancelInvoicePending ||
                      isPostSendInvoiceEmailPending
                    }
                    className="h-12"
                    color="primary"
                    onPress={() => {
                      openChangePaymentsModal();
                    }}
                    data-identifier="open-change-payments"
                    startContent={<Replace size={16} />}
                  >
                    <Trans>Spremeni način plačila</Trans>
                  </Button>
                ) : undefined}
                {(invoice?._documentClient.companyData != null ||
                  invoice?._documentClient.customerData != null) && (
                  <Button
                    isDisabled={
                      isPending ||
                      isError ||
                      isPrinting ||
                      isPostCancelInvoicePending
                    }
                    isLoading={isPostSendInvoiceEmailPending}
                    className="h-12"
                    color="primary"
                    startContent={<Send size={16} />}
                    onPress={async () => {
                      const customerEmail =
                        invoice?._documentClient.customerData?.email;
                      const companyEmail =
                        invoice?._documentClient.companyData?.email;

                      const email = customerEmail ?? companyEmail;

                      if (!invoiceId || !email) return;

                      await postSendInvoiceEmail({
                        invoiceId,
                        body: {
                          recipientEmail: email,
                        },
                      });
                    }}
                  >
                    <Trans>Pošlji po e-pošti</Trans>
                  </Button>
                )}

                <Button
                  isDisabled={
                    isPending ||
                    isError ||
                    isPrinting ||
                    isGetDownloadInvoicePending
                  }
                  isLoading={isGetDownloadInvoicePending}
                  className="h-12"
                  color="primary"
                  startContent={<Download size={16} />}
                  onPress={async () => {
                    if (!invoiceId) return;

                    const response = await getDownloadInvoice({
                      invoiceId,
                    });

                    if (!response) return;

                    // Convert base64 string to binary data
                    const binaryString = atob(response);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; i++) {
                      bytes[i] = binaryString.charCodeAt(i);
                    }

                    const blob = new Blob([bytes], {
                      type: "application/pdf",
                    });

                    const url = URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `invoice_${invoice?.number}.pdf`;

                    document.body.appendChild(link);

                    link.click();
                    link.parentNode?.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >
                  {invoice?.type === "invoice" ? (
                    <Trans>Prenesi račun</Trans>
                  ) : (
                    <Trans>Prenesi predračun</Trans>
                  )}
                </Button>

                {invoice?.canceled ? undefined : (
                  <>
                    <Divider className="my-2" />

                    <form
                      onSubmit={form.onSubmit(async (values) => {
                        if (!invoiceId) return;

                        const response = await postCancelInvoice({
                          invoiceId,
                          body: values,
                        });

                        if (response.type === "estimate") {
                          onClose();
                        } else {
                          navigate(`../${response.id}`, {
                            relative: "path",
                          });
                        }
                      })}
                      className="flex flex-col gap-2"
                    >
                      {invoice?.type === "estimate" ? null : (
                        <LimeTextarea
                          placeholder={t`Razlog stornacije`}
                          variant="bordered"
                          {...form.getInputProps("cancellationReason")}
                          data-identifier="cancellation-reason"
                        />
                      )}
                      <Button
                        isDisabled={
                          isPending ||
                          isError ||
                          isPrinting ||
                          isPostSendInvoiceEmailPending
                        }
                        isLoading={isPostCancelInvoicePending}
                        className="h-12"
                        color="primary"
                        type="submit"
                        data-identifier="cancel-invoice"
                        startContent={<FileX2 size={16} />}
                      >
                        <Trans>Storniraj</Trans>
                      </Button>
                    </form>
                  </>
                )}

                <LimeAlert
                  color="danger"
                  message={postSendInvoiceEmailErrorMessage}
                />
                <LimeAlert
                  color="danger"
                  message={postCancelInvoiceErrorMessage}
                />
                <LimeAlert color="danger" message={getInvoiceErrorMessage} />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {invoice != null && (
        <SplitPaymentModal
          existingData={invoice.additionalReceiptData.payments.map(
            (payment) => {
              if (payment.type === "coupon") {
                if (payment.isThirdParty === true) {
                  return {
                    type: "coupon" as const,
                    isThirdParty: true as const,
                    amountCents: payment.amount * 100,
                    giftCardCode: payment.code?.split("-")[1] ?? "N/A",
                    giftCardPrefix: payment.code?.split("-")[0] ?? "N/A",
                  };
                } else {
                  return {
                    type: "coupon" as const,
                    isThirdParty: false as const,
                    amountCents: payment.amount * 100,
                    giftCardCode: payment.code ?? "N/A",
                    isLinkedToAppointment: false,
                  };
                }
              }

              return {
                type: payment.type as Exclude<
                  SplitPaymentForm["options"][number]["type"],
                  "coupon"
                >,
                amountCents: payment.amount * 100,
              };
            },
          )}
          handleClose={async (data) => {
            if (!data) {
              closeChangePaymentsModal();
              return;
            }

            const payments = data.options
              .filter(
                (option) =>
                  option.amountCents && Number(option.amountCents) > 0,
              ) // Filter entries where the value is valid and greater than 0
              .map((option) => {
                const type = option.type;

                if (type === "coupon") {
                  if (option.isThirdParty) {
                    return {
                      type,
                      isThirdParty: true as const,
                      amountCents: Number(option.amountCents),
                      giftCardCode: option.giftCardCode,
                      giftCardPrefix: option.giftCardPrefix,
                    };
                  }

                  return {
                    type,
                    isThirdParty: false as const,
                    amountCents: Number(option.amountCents),
                    giftCardCode: option.giftCardCode,
                  };
                }

                return {
                  type,
                  amountCents: Number(option.amountCents),
                };
              });

            await putInvoicePaymentMethod({
              invoiceId: invoice.id,
              body: {
                payments,
              },
            });

            refetch();
            closeChangePaymentsModal();
          }}
          isOpen={isChangePaymentsModalOpen}
          isLoading={isPutInvoicePaymentMethodPending}
          errorMessage={putInvoicePaymentMethodErrorMessage}
          totalPriceCents={invoice.totalWithTax * 100}
          locale="sl"
          currency="EUR"
          hideCoupon
          isChangingPaymentOnExistingInvoice
        />
      )}
    </>
  );
};

const InvoiceDetailRow = ({
  label,
  value,
  isSkeleton,
}: {
  label: string;
  value?: string | React.ReactNode;
  isSkeleton?: boolean;
}) => {
  return (
    <div className="flex w-full justify-between">
      <Skeleton isLoaded={!isSkeleton}>
        <p className="text-xs font-semibold">{label}</p>
      </Skeleton>
      <Skeleton isLoaded={!isSkeleton}>
        {typeof value === "string" ? (
          <p className="text-sm text-gray-500">{value}</p>
        ) : (
          value
        )}
      </Skeleton>
    </div>
  );
};
